import React, { useState, useEffect } from 'react';
import './Preferences.css';
import { API_BASE_URL } from '../config';
import { useToast } from '../contexts/ToastContext';
import { X } from 'phosphor-react';

interface PreferencesProps {
  user: any;
}

interface Baby {
  id: string;
  name: string;
}

interface SleepPreferences {
  id?: string;
  baby_id: string;
  target_nap_count: number | null;
  preferred_bedtime: string | null; // HH:MM format
  preferred_wake_time: string | null; // HH:MM format
  baby_age_weeks?: number | null;
  typical_nap_min?: number | null;
  typical_nap_max?: number | null;
}

const Preferences: React.FC<PreferencesProps> = ({ user }) => {
  const [activeBaby, setActiveBaby] = useState<Baby | null>(null);
  const [preferences, setPreferences] = useState<SleepPreferences | null>(null);
  const [targetNapCount, setTargetNapCount] = useState<number | null>(null);
  const [preferredBedtime, setPreferredBedtime] = useState<string>('');
  const [preferredWakeTime, setPreferredWakeTime] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  // Fetch active baby
  useEffect(() => {
    const fetchActiveBaby = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
        if (response.ok) {
          const babies = await response.json();
          const active = babies.find((b: any) => b.id === user.active_baby_id);
          setActiveBaby(active);
        }
      } catch (error) {
        console.error('Error fetching active baby:', error);
        showToast('Failed to load baby information', 'error');
      }
    };

    fetchActiveBaby();
  }, [user.id, user.active_baby_id, showToast]);

  // Fetch preferences when active baby changes
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!activeBaby) return;

      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/sleep-preferences/${activeBaby.id}`);
        if (response.ok) {
          const data = await response.json();
          setPreferences(data);
          setTargetNapCount(data.target_nap_count);
          setPreferredBedtime(data.preferred_bedtime || '');
          setPreferredWakeTime(data.preferred_wake_time || '');
        } else if (response.status === 404) {
          // No preferences exist yet
          setPreferences(null);
          setTargetNapCount(null);
          setPreferredBedtime('');
          setPreferredWakeTime('');
        } else {
          showToast('Failed to load preferences', 'error');
        }
      } catch (error) {
        console.error('Error fetching preferences:', error);
        showToast('Failed to load preferences', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [activeBaby, showToast]);

  const handleSave = async () => {
    if (!activeBaby) return;

    setSaving(true);
    try {
      const payload: any = {
        target_nap_count: targetNapCount,
        preferred_bedtime: preferredBedtime || null,
        preferred_wake_time: preferredWakeTime || null
      };

      let response;
      if (preferences) {
        // Update existing
        response = await fetch(`${API_BASE_URL}/api/sleep-preferences/${activeBaby.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        // Create new
        payload.baby_id = activeBaby.id;
        response = await fetch(`${API_BASE_URL}/api/sleep-preferences/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
        showToast('Preferences saved successfully', 'success');
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to save preferences', 'error');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      showToast('Failed to save preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!activeBaby || !preferences) return;

    if (!window.confirm('Are you sure you want to clear all sleep preferences?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep-preferences/${activeBaby.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setPreferences(null);
        setTargetNapCount(null);
        setPreferredBedtime('');
        setPreferredWakeTime('');
        showToast('Preferences cleared', 'success');
      } else {
        showToast('Failed to clear preferences', 'error');
      }
    } catch (error) {
      console.error('Error clearing preferences:', error);
      showToast('Failed to clear preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="preferences-page">
        <div className="loading">Loading preferences...</div>
      </div>
    );
  }

  return (
    <div className="preferences-page">
      <div className="preferences-content">
        <h1>Sleep Preferences</h1>

        {activeBaby && (
          <div className="baby-indicator">
            <p>Preferences for <strong>{activeBaby.name}</strong></p>
          </div>
        )}

        <div className="preferences-section">
          <p className="section-description">
            Set your sleep goals for AI predictions. The AI will gradually work toward these
            preferences while respecting your baby's natural patterns.
          </p>

          <div className="preference-item">
            <label htmlFor="nap-count">Target naps per day</label>
            <p className="item-description">How many naps would you like your baby to have?</p>
            <select
              id="nap-count"
              value={targetNapCount === null ? '' : targetNapCount}
              onChange={(e) => setTargetNapCount(e.target.value === '' ? null : parseInt(e.target.value))}
              disabled={saving}
            >
              <option value="">No preference</option>
              <option value="0">0 naps</option>
              <option value="1">1 nap</option>
              <option value="2">2 naps</option>
              <option value="3">3 naps</option>
              <option value="4">4 naps</option>
              <option value="5">5 naps</option>
            </select>
            {preferences?.baby_age_weeks !== null &&
             preferences?.baby_age_weeks !== undefined &&
             preferences?.typical_nap_min !== null &&
             preferences?.typical_nap_max !== null && (
              <p className="item-description" style={{ marginTop: '8px', fontStyle: 'italic', color: '#666' }}>
                A baby at {preferences.baby_age_weeks} weeks normally has between {preferences.typical_nap_min} to {preferences.typical_nap_max} naps
              </p>
            )}
          </div>

          <div className="preference-item">
            <label htmlFor="bedtime">Preferred bedtime</label>
            <p className="item-description">What time would you like your baby to go to sleep at night?</p>
            <div className="time-input-wrapper">
              <input
                type="time"
                id="bedtime"
                value={preferredBedtime}
                onChange={(e) => setPreferredBedtime(e.target.value)}
                disabled={saving}
              />
              {preferredBedtime && (
                <button
                  className="clear-time-btn"
                  onClick={() => setPreferredBedtime('')}
                  title="Clear bedtime"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="preference-item">
            <label htmlFor="wake-time">Preferred wake time</label>
            <p className="item-description">What time would you like your baby to wake up in the morning?</p>
            <div className="time-input-wrapper">
              <input
                type="time"
                id="wake-time"
                value={preferredWakeTime}
                onChange={(e) => setPreferredWakeTime(e.target.value)}
                disabled={saving}
              />
              {preferredWakeTime && (
                <button
                  className="clear-time-btn"
                  onClick={() => setPreferredWakeTime('')}
                  title="Clear wake time"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="preference-actions">
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>

            {preferences && (
              <button
                className="clear-btn"
                onClick={handleClear}
                disabled={saving}
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preferences;
